import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { courseDetailQueryOptions, courseReviewsQueryOptions } from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { PageQuery } from '@/schemas/courses'
import { parseId } from '@/screens/courses/lib/params'
import { pageParamSchema } from '@/screens/courses/search'

/** The course page's tabs, in the order they are drawn. */
export const COURSE_TABS = ['lectures', 'details', 'reviews'] as const
export type CourseTab = (typeof COURSE_TABS)[number]

/**
 * The tabs this page had before it had three: a link sent from one still
 * lands on the tab that now holds what it pointed at. Instructor and
 * Materials are sections of Details now.
 */
const LEGACY_TABS: Readonly<Record<string, CourseTab>> = {
  overview: 'details',
  curriculum: 'lectures',
  instructor: 'details',
  materials: 'details',
}

export const courseTabSchema = z.preprocess(
  (value) => (typeof value === 'string' ? (LEGACY_TABS[value] ?? value) : value),
  z.enum(COURSE_TABS),
)

/**
 * - `tab` — which tab is open; Lectures by default.
 * - `page` — the Reviews tab's page. Ignored by every other tab.
 */
export const courseDetailSearchSchema = z.object({
  tab: courseTabSchema.default('lectures').catch('lectures'),
  page: pageParamSchema,
})
export type CourseDetailSearch = z.infer<typeof courseDetailSearchSchema>

export const COURSE_DETAIL_SEARCH_DEFAULTS = { tab: 'lectures', page: 1 } as const

/** Reviews per page — fixed; the tab has no page-size control. */
export const REVIEWS_PAGE_SIZE = 10

/** The reviews read for a URL — the one place its key is built, for loader and screen alike. */
export const reviewsPageQuery = (search: CourseDetailSearch): PageQuery => ({
  page: search.page,
  pageSize: REVIEWS_PAGE_SIZE,
})

/**
 * The `/courses/$courseId` loader.
 *
 * MAIN: the course with its lectures — the header, the call to action and
 * every tab but Reviews read it. `null` (`COURSE_NOT_FOUND`) is the screen's
 * not-found state, never this route's error page.
 *
 * SECONDARY, only for a real navigation to `?tab=reviews`: that page of
 * reviews, so a shared link lands with its tab already loading. A tab click
 * is a search-only change and runs none of this.
 *
 * A `$courseId` that is not a positive integer (`/courses/abc`) is answered
 * without a request: the loader returns, and the screen renders not-found
 * before its suspense read.
 */
export async function loadCourseDetail({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { courseId: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const courseId = parseId(params.courseId)
  if (courseId === null) return
  const search = courseDetailSearchSchema.parse(location.search)
  if (!preload && search.tab === 'reviews') {
    prefetchRouteData(queryClient, courseReviewsQueryOptions(courseId, reviewsPageQuery(search)))
  }
  await ensureRouteData(queryClient, courseDetailQueryOptions(courseId))
}
